import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  RedirectfulResponse,
  SwapAuthForAccessTokens
} from '../../../models';
import { LoginRedirectfulService } from '../login-remember-me-adapter-service/login-redirectful/login-redirectful.service';
import {
  checkRequestIsDefined,
  getResponseId,
  isRequestInputHasProperties,
  isUserNamePasswordRequired,
  transformSwapAuthTokenDetails
} from '../login-remember-me-utils-service/initiate-login-utils';
import { LoginRememberMeErrorUtilService } from '../login-remember-me-utils-service/login-remember-me-error-util.service';
import { LoginRememberMeUtilsService } from '../login-remember-me-utils-service/login-remember-me-utils.service';
import { RedirectfulPostWithCredentialsFacadeService } from './redirectful-post-with-credentials-facade.service';
@Injectable({
  providedIn: 'root'
})
export class RedirectfulFacadeService {
  constructor(
    private loginRedirectful: LoginRedirectfulService,
    private loginRememberMeUtils: LoginRememberMeUtilsService,
    private loginRememberMeErrorUtil: LoginRememberMeErrorUtilService,
    private redirectfulPostWithCredentialsFacade: RedirectfulPostWithCredentialsFacadeService
  ) {}

  initiateRedirectFulLogin(
    loginOAuthParams: LoginOAuthParams,
    flowId: string,
    requestInput?: ILoginUserNamePasswordInput
  ): Observable<
    AuthAccessTokenDetails | RedirectfulResponse | boolean | string
  > {
    return this.loginRedirectful
      .loginRedirectful(loginOAuthParams.oauthConfig, flowId)
      .pipe(
        concatMap((response: LoginOAuthResponse) => {
          this.redirectfulPostWithCredentialsFacade.flowId =
            getResponseId(response);
          if (
            response.status ===
            loginOAuthParams.oauthConfig.USERNAME_PASSWORD_REQUIRED
          ) {
            return of(response);
          } else if (
            response.status === loginOAuthParams.oauthConfig.COMPLETED
          ) {
            return this.loginRememberMeUtils.swapAuthCodeForToken(
              loginOAuthParams,
              response.authorizeResponse?.code
            );
          } else {
            return throwError(response);
          }
        }),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens
          ) => {
            if (isUserNamePasswordRequired(response, loginOAuthParams)) {
              return this.checkRequestInput(
                loginOAuthParams,
                response,
                checkRequestIsDefined(requestInput)
              );
            } else if ('access_token' in response) {
              return of(response);
            } else {
              return throwError(response);
            }
          }
        ),
        concatMap(
          (
            response:
              | LoginAuthWithCredentialsResponse
              | SwapAuthForAccessTokens
              | RedirectfulResponse
              | string
          ) => this.responseHandler(response, loginOAuthParams)
        ),
        catchError((error) => {
          return of(
            this.loginRememberMeErrorUtil.checkRememberMeErrors(
              loginOAuthParams.oauthConfig,
              error
            )
          );
        })
      );
  }

  responseHandler(
    response:
      | RedirectfulResponse
      | SwapAuthForAccessTokens
      | LoginAuthWithCredentialsResponse
      | string,
    loginOAuthParams: LoginOAuthParams
  ) {
    if (typeof response === 'string') {
      return throwError(response);
    } else {
      return this.handleResponse(response, loginOAuthParams);
    }
  }

  handleResponse(
    response:
      | RedirectfulResponse
      | SwapAuthForAccessTokens
      | LoginAuthWithCredentialsResponse,
    loginOAuthParams: LoginOAuthParams
  ): Observable<boolean | AuthAccessTokenDetails | RedirectfulResponse> {
    if (isUserNamePasswordRequired(response, loginOAuthParams)) {
      return of(true);
    } else if ('access_token' in response) {
      return of(transformSwapAuthTokenDetails(response));
    } else if ('resumeUrl' in response) {
      return of(response as RedirectfulResponse);
    } else {
      return throwError(response);
    }
  }

  checkRequestInput(
    loginOAuthParams: LoginOAuthParams,
    response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<
    LoginAuthWithCredentialsResponse | RedirectfulResponse | string
  > {
    if (isRequestInputHasProperties(requestInput)) {
      return this.redirectfulPostWithCredentialsFacade.redirectfulPostWithCredFlow(
        loginOAuthParams,
        requestInput
      );
    } else {
      return of(response);
    }
  }
}
